function getDisplayName<T>(WrappedComponent: React.ComponentType<T>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export type MODIFIED_PROPS<T extends object = object> = {
  [k in keyof T]: {
    properties?: Record<string, T[keyof T]>;
    defaultValue?: T[keyof T];
    variations?: {
      [p in keyof T[k]]: Record<string, unknown>;
    };
  };
};

export const modifyProps = <T extends object>(
  props: T,
  map?: (props: T) => Partial<MODIFIED_PROPS<T>>
) => {
  if (!props) return {} as T;
  if (!map) return props as T;
  let finalProps: T = {
    ...props
  };

  const modifedProps = map(props);

  Object.entries(modifedProps).forEach(([key, obj]) => {
    const { properties, variations, defaultValue } = obj as MODIFIED_PROPS<
      Record<string, string>
    >[typeof key];

    const currentValue = props[key as keyof T];
    if (typeof currentValue !== 'undefined') {
      if (Object.keys(properties || {}).length) {
        // delete finalProps[key as keyof T];
        finalProps = {
          ...finalProps,
          ...properties
        };
      }

      if (variations?.[currentValue]) {
        finalProps = {
          ...finalProps,
          ...variations?.[currentValue]
        };
      }
    } else {
      if (defaultValue) {
        finalProps = {
          ...finalProps,
          ...variations?.[defaultValue as T[keyof T]]
        };
      }
    }
  });
  return finalProps;
};

export const withModifiedProps =
  <T extends object = Record<string, string>>(
    Component: React.ComponentType<T>,
    map?: (props: T) => Partial<MODIFIED_PROPS<T>>
  ): React.FC<T> =>
  (props: T) => {
    if (!map) return <Component {...props} />;

    Component.displayName = getDisplayName(Component);
    const finalProps = modifyProps(props, map);

    return <Component {...props} {...finalProps} />;
  };

export type IPROP_MAP<T extends object> = (props: T) => MODIFIED_PROPS<T>;
